import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'wouter';
import { ProjectPlan } from '@platform/client';
import { AvatarNew, addToast, Icon, type StepConfig, VerticalStepper } from '@platform/components';
import { AuthedPage, HeaderFooterLayout } from '../app';
import { BeforeTheCallSection } from './components/BeforeTheCallSection';
import { DuringTheCallSection } from './components/DuringTheCallSection';
import { FinalPlanSection } from './components/FinalPlanSection';
import { PlanAccordion } from './components/PlanAccordion';

const UserAvatar = ({ firstName, lastName, avatar }: { firstName: string; lastName: string; avatar?: string | null }) => {
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName ?
    <AvatarNew size='md' url={avatar} name={fullName} alt={fullName} />
    : <AvatarNew size='md' url={avatar} fallbackIcon='user-01' alt='' />;
};

// Two overlapping avatars for During the Call
const DuoAvatar = ({
  repAvatar,
  proAvatar,
  repName,
  proName,
  isTeam,
}: {
  repAvatar?: string | null;
  proAvatar?: string | null;
  repName?: string;
  proName?: string;
  isTeam?: boolean;
}) => (
  <div className='relative h-8 w-14 shrink-0'>
    <div className='absolute left-0 top-0'>
      {repName?.trim() ?
        <AvatarNew size='sm' url={repAvatar} name={repName} alt={repName} />
        : <AvatarNew size='sm' url={repAvatar} fallbackIcon='user-01' alt='' />}
    </div>
    <div className='absolute left-6 top-0 rounded-full ring-2 ring-white'>
      {isTeam
        ? <AvatarNew size='sm' fallbackIcon='users-02' alt='' />
        : proName?.trim()
          ? <AvatarNew size='sm' url={proAvatar} name={proName} alt={proName} />
          : <AvatarNew size='sm' url={proAvatar} fallbackIcon='user-01' alt='' />}
    </div>
  </div>
);

// Checkmark circle for Final Plan
const CheckAvatar = ({ isActive }: { isActive?: boolean }) => (
  <div
    className={`flex shrink-0 items-center justify-center rounded-full ${isActive ? 'h-8 w-8 bg-success-25' : 'h-8 w-8 bg-gray-100'}`}
  >
    <Icon
      type='check-circle'
      size={isActive ? '32' : '20'}
      stroke={isActive ? 'success-500' : 'gray-400'}
    />
  </div>
);

export const ProjectPlanPage = () => {
  const { planId } = useParams<{ planId: string }>();
  const { data: planData } = ProjectPlan.useGetProjectPlanById(Number(planId));

  const [beforeComplete, setBeforeComplete] = useState(false);
  const [duringComplete, setDuringComplete] = useState(false);
  const [openSection, setOpenSection] = useState<'before' | 'during' | 'final' | null>('before');

  const duringRef = useRef<HTMLDivElement>(null);
  const finalRef = useRef<HTMLDivElement>(null);
  const initialSectionSet = useRef(false);

  useEffect(() => {
    if (openSection === 'during') {
      duringRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (openSection === 'final') {
      finalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [openSection]);

  const duringSavedAt = planData?.updatedAt
    ? new Date(planData.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const isProfessional = planData?.isTeam
    ? (planData?.teamMembers ?? []).some((m) => m.id === planData?.currentUserId)
    : planData?.userRole === 'PROFESSIONAL';

  const isCurrentUserAlignmentSaved = planData?.isTeam
    ? !!(planData?.skillAlignments?.find((s) => s.userId === planData?.currentUserId)?.skillsAlignment?.trim())
    : (planData?.skillAlignments?.length ?? 0) > 0;

  useEffect(() => {
    if (isProfessional) {
      // Team pro: complete only when THIS user's skill alignment is saved
      // Individual pro: complete when any skill alignment is saved
      if (isCurrentUserAlignmentSaved) {
        setBeforeComplete(true);
      }
    } else if (planData?.organizationBrief) {
      // Rep (individual or team): complete when org brief is saved
      setBeforeComplete(true);
    }
  }, [planData?.organizationBrief, isCurrentUserAlignmentSaved, isProfessional]);

  useEffect(() => {
    if (planData?.communicationNotes) {
      setDuringComplete(true);
    }
  }, [planData?.communicationNotes]);

  useEffect(() => {
    if (initialSectionSet.current || !planData) return;
    initialSectionSet.current = true;
    const beforeDone = isProfessional
      ? isCurrentUserAlignmentSaved
      : !!planData.organizationBrief;
    const duringDone = !!planData.communicationNotes;
    if (beforeDone && duringDone) {
      setOpenSection('final');
    } else if (beforeDone) {
      setOpenSection('during');
    }
  }, [planData, isProfessional, isCurrentUserAlignmentSaved]);

  const beforeContact = isProfessional
    ? planData?.isTeam
      ? (planData.teamMembers ?? []).find((m) => m.id === planData.currentUserId) ?? planData.teamMembers?.[0]
      : planData?.professional ?? undefined
    : planData?.representative;

  const steps: StepConfig[] = [
    {
      id: 'before-the-call',
      label: 'Before the Call',
      description: 'Complete on your own',
      status: beforeComplete ? 'completed' : 'active',
    },
    {
      id: 'during-the-call',
      label: 'During the call',
      description: 'Complete together',
      status: duringComplete ? 'completed' : beforeComplete ? 'active' : 'upcoming',
    },
    {
      id: 'final-plan',
      label: 'Final Plan',
      description: 'Result of Planning Call',
      status: duringComplete ? 'completed' : 'upcoming',
    },
  ];

  const displayToast = () => {
    addToast({
      description: 'This section is complete! You can update it anytime',
    });
  };

  return (
    <AuthedPage>
      <HeaderFooterLayout useDashboardHeader withSidebar={false}>
        <div className="mx-auto max-w-[1280px] px-4 pb-16 sm:px-6 md:px-10">
          {/* Back link */}
          <div className='mb-6 md:mb-10'>
            <Link
              href={
                planData?.isTeam
                  ? `/team-project-hub/${planData.projectId}`
                  : `/project-hub/${planData?.assignmentDiscoveryId}`
              }
              className="flex w-fit items-center gap-2 no-underline text-brand-500 hover:opacity-80"
            >
              <Icon type='arrow-left' size='20' additionalClassNames='stroke-brand-500' />
              <span className="text-base font-semibold leading-6">
                Back
              </span>
            </Link>
          </div>

          {/* Page header */}
          <div className='mb-6 flex flex-col gap-3 md:mb-10'>
            <h1 className="text-[28px] font-semibold leading-[36px] tracking-[-0.56px] text-gray-700 sm:text-[36px] sm:leading-[44px] sm:tracking-[-0.72px]">
              Project Plan
            </h1>
            <p className="text-base font-normal leading-6 text-gray-500 sm:text-lg sm:leading-7">
              This page helps you prepare for your planning call and capture key decisions during the
              conversation.
            </p>
          </div>

          {/* Main layout */}
          <div className='flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8'>
            {/* Stepper — full width on mobile, fixed sidebar on desktop */}
            <div className='w-full shrink-0 rounded-xl border border-gray-100 bg-white p-4 sm:p-6 lg:w-60'>
              <VerticalStepper
                steps={steps}
                onStepClick={(id) => {
                  if (id === 'before-the-call') setOpenSection('before');
                  else if (id === 'during-the-call') setOpenSection('during');
                  else if (id === 'final-plan') setOpenSection('final');
                }}
              />
            </div>

            {/* Right: accordion stack */}
            <div className='flex flex-1 flex-col gap-6 md:gap-8'>
              {/* Accordion 1: Before the Call */}
              <PlanAccordion
                icon={
                  planData?.isTeam && isProfessional
                    ? <AvatarNew size='md' fallbackIcon='users-02' alt='' />
                    : <UserAvatar
                      firstName={beforeContact?.firstname ?? ''}
                      lastName={beforeContact?.lastname ?? ''}
                      avatar={beforeContact?.avatar}
                    />
                }
                title='Before the call - Complete on your own'
                subtitle={isProfessional
                  ? 'Share your skills and experience. Add any questions you have before the planning call.'
                  : planData?.organizationName}
                isCompleted={beforeComplete}
                isOpen={openSection === 'before'}
                onOpenChange={(open) => setOpenSection(open ? 'before' : null)}
              >
                <BeforeTheCallSection
                  key={`before-${planData?.id ?? 'loading'}`}
                  planId={Number(planId)}
                  initialData={planData?.organizationBrief}
                  initialProBrief={planData?.proBrief}
                  savedAt={planData?.beforeUpdatedAt ?? undefined}
                  isCompleted={beforeComplete}
                  onComplete={() => { setBeforeComplete(true); setOpenSection('during'); displayToast(); }}
                  onEdit={() => setBeforeComplete(false)}
                  isTeam={planData?.isTeam}
                  isProfessional={isProfessional}
                  professionalId={planData?.professional?.id}
                  currentUserId={planData?.currentUserId ?? undefined}
                  teamMembers={planData?.teamMembers}
                  initialSkillAlignments={planData?.skillAlignments}
                  projectDescription={planData?.projectDescription}
                />
              </PlanAccordion>

              {/* Accordion 2: During the Call */}
              <div ref={duringRef}>
                <PlanAccordion
                  icon={
                    <DuoAvatar
                      isTeam={planData?.isTeam}
                      repAvatar={planData?.representative?.avatar}
                      proAvatar={planData?.professional?.avatar}
                      repName={
                        planData?.representative ?
                          `${planData.representative.firstname} ${planData.representative.lastname}`
                          : undefined
                      }
                      proName={
                        planData?.professional ?
                          `${planData.professional.firstname} ${planData.professional.lastname}`
                          : undefined
                      }
                    />
                  }
                  title='During the call - Complete together'
                  subtitle='One person fills this out during your planning call while you both discuss'
                  isCompleted={duringComplete}
                  isOpen={openSection === 'during'}
                  onOpenChange={(open) => setOpenSection(open ? 'during' : null)}
                >
                  <DuringTheCallSection
                    key={`during-${planData?.id ?? 'loading'}`}
                    planId={Number(planId)}
                    initialData={planData ?? null}
                    isCompleted={duringComplete}
                    isProfessional={isProfessional}
                    onComplete={() => { setDuringComplete(true); setOpenSection('final'); displayToast(); }}
                    onEdit={() => setDuringComplete(false)}
                  />
                </PlanAccordion>
              </div>

              {/* Accordion 3: Final Project Plan */}
              <div ref={finalRef}>
                <PlanAccordion
                  key={`final-${duringComplete}`}
                  icon={<CheckAvatar isActive={duringComplete} />}
                  title='Final Project Plan'
                  subtitle='Here’s what you aligned on together'
                  containerClassName={duringComplete ? 'bg-success-25' : 'bg-white'}
                  customBorderClass={duringComplete ? 'border-success-500' : undefined}
                  isOpen={openSection === 'final'}
                  onOpenChange={(open) => setOpenSection(open ? 'final' : null)}
                >
                  {duringComplete && planData ? (
                    <FinalPlanSection
                      planData={planData}
                      savedDate={duringSavedAt}
                      onEditPlan={() => { setDuringComplete(false); setOpenSection('during'); }}
                    />
                  ) : (
                    <p className="text-sm text-gray-500">
                      Your final project plan summary will appear here after your planning call.
                    </p>
                  )}
                </PlanAccordion>
              </div>
            </div>
          </div>
        </div>
      </HeaderFooterLayout>
    </AuthedPage>
  );
};
